import React, { useState, useMemo } from "react";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { Calculator as CalcIcon } from "lucide-react";

export default function Calculator() {
  const [capital, setCapital] = useState(10000);
  const [riskPct, setRiskPct] = useState(1);
  const [entry, setEntry] = useState(100);
  const [stop, setStop] = useState(96);
  const [target, setTarget] = useState(108);

  const result = useMemo(() => {
    const cap = parseFloat(capital) || 0;
    const rp = parseFloat(riskPct) || 0;
    const en = parseFloat(entry) || 0;
    const sl = parseFloat(stop) || 0;
    const tp = parseFloat(target) || 0;
    const riskAmount = cap * (rp / 100);
    const perUnitRisk = Math.abs(en - sl);
    const units = perUnitRisk > 0 ? riskAmount / perUnitRisk : 0;
    const positionValue = units * en;
    const rewardPerUnit = Math.abs(tp - en);
    const rr = perUnitRisk > 0 ? rewardPerUnit / perUnitRisk : 0;
    return {
      riskAmount, units, positionValue, rr,
      valid: en > 0 && sl > 0 && en !== sl,
    };
  }, [capital, riskPct, entry, stop, target]);

  const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  const inputs = [
    ["Account capital (virtual $)", capital, setCapital],
    ["Risk per trade (%)", riskPct, setRiskPct],
    ["Entry price ($)", entry, setEntry],
    ["Stop Loss price ($)", stop, setStop],
    ["Take Profit price ($)", target, setTarget],
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/20 grid place-items-center">
          <CalcIcon className="w-6 h-6 text-blue-300" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Risk & Position Size Calculator</h1>
          <p className="text-slate-400 text-sm mt-0.5">Learn how much to trade based on the risk you accept.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-6 space-y-4">
          {inputs.map(([label, val, set], i) => (
            <div key={i} className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">{label}</label>
              <input type="number" data-testid={`calc-input-${i}`} value={val} onChange={(e) => set(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm font-mono focus:border-blue-500 focus:outline-none" />
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-slate-900 to-[#0B1120] border border-slate-800 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Position size</div>
            <div className="font-mono text-3xl font-bold text-blue-400" data-testid="calc-position-size">{result.valid ? fmt(result.units) : "—"}</div>
            <div className="text-sm text-slate-400 mt-1">units · ≈ ${result.valid ? fmt(result.positionValue) : "—"} position value</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Max loss</div>
              <div className="font-mono text-2xl font-bold text-bear" data-testid="calc-max-loss">${fmt(result.riskAmount)}</div>
            </div>
            <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Risk / Reward</div>
              <div className="font-mono text-2xl font-bold text-bull" data-testid="calc-rr">1 : {result.valid ? result.rr.toFixed(2) : "—"}</div>
            </div>
          </div>
          {!result.valid && <p className="text-xs text-amber-400">Enter a valid entry and stop loss (they must differ).</p>}
        </div>
      </div>

      <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-5 text-sm text-slate-400 leading-relaxed">
        <span className="font-semibold text-slate-200">How it works:</span> Position size = (Capital × Risk%) ÷ (Entry − Stop Loss). By risking only a small percentage per trade, no single loss can seriously damage your account.
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}
